import { Component, Inject, OnInit } from '@angular/core';
import { DOCUMENT } from '@angular/common';
import { MenuItem, MessageService } from 'primeng/api';
import { EMPTY, Subject, merge, of } from 'rxjs';
import { catchError, filter, map, switchMap, take, timeout } from 'rxjs/operators';

import { ResultSearchService } from '../../../services/result-search.service';
import { ResultSearchRequest, ResultSearchRow } from '../../models/result-search';
import { ProcessType } from '../../models/request';
import { JIT_TOOLS } from '../../models/jit-tool-registry';
import { getToolByServerProcessType } from '../../models/jit-tool-registry.helpers';
import { iconForFileName } from '../../main.utilities';
import { MainFacade } from '../../main.facade';
import { FormatFileSizePipe } from '../../pipes/format-file-size.pipe';

const PAGE_SIZE = 50;

interface ToolOption {
  label: string;
  value: ProcessType;
}

/**
 * The "Results" tab (jit-ui#135, Phase 2 of the Results-search feature -
 * doc/JIT-Results.md Frontend Design, jit-parent#38/#39). Lets a user
 * search their own completed runs' result files by name/tool/date and open
 * one by clicking its row - one row per result file, not per run (a run
 * that produced two files is two rows).
 *
 * Paging is "load more", not a numbered pager: `POST /results/search`
 * returns only the requested page, with no total-match count to size a
 * real paginator against - see doc/JIT-Results.md Backend Design §3. A
 * page that comes back full (`PAGE_SIZE` rows) is treated as "there might
 * be more"; a short page means we've reached the end.
 */
@Component({
  selector: 'results-tab',
  templateUrl: './result-search.component.html',
  styleUrls: ['./result-search.component.scss'],
})
export class ResultSearchComponent implements OnInit {

  rows: ResultSearchRow[] = [];
  loading = false;
  hasMore = false;
  selectedRow: ResultSearchRow | null = null;
  scope: 'mine' | 'all' = 'mine';

  fileName = '';
  tool: ProcessType | null = null;
  from: Date | null = null;
  to: Date | null = null;

  // Client-facing processType, not getServerProcessType() - the backend
  // filters against AbstractStatus.info (ProcessInfo), a status-reporting
  // enum that only ever has e.g. ALIGNMENT, never a submission-time alias
  // like SIFT. See models/result-search.ts's ResultSearchRequest.tool doc.
  readonly toolOptions: ToolOption[] = JIT_TOOLS
    .filter((t) => !t.hiddenFromProcessDropdown)
    .map((t) => ({ label: t.displayName, value: t.processType }));

  /** The table's right-click menu - the same four actions as the toolbar
   *  above (Clear/Mine-All/Refresh/Show in Files Tab). NOT a getter: binding
   *  [model] straight to a getter handed PrimeNG a brand-new array of
   *  brand-new MenuItem objects on every change-detection tick (e.g. every
   *  mouse move over the open menu), and since ContextMenu/*ngFor diff items
   *  by reference, it kept destroying and recreating the menu's DOM out from
   *  under the pointer - items showed correctly but clicks never landed
   *  because the element a mousedown started on was gone by mouseup. Building
   *  it once, right before the menu opens (see refreshRowMenuItems(), wired
   *  to the table's (contextmenu) handler), keeps the array stable while
   *  the menu is up. */
  rowMenuItems: MenuItem[] = [];

  private pageNo = 0;

  /** One long-lived pipeline for every search/page request (Clear, scope
   *  toggle, Refresh, Search, and Load More all funnel through loadPage()
   *  into here). switchMap cancels a still-in-flight request the moment a
   *  newer one arrives - without this, Search/Clear/scope/Refresh stayed
   *  clickable while a request was loading, and a slow older request could
   *  land after a newer one and overwrite its rows/loading/hasMore with
   *  stale data (jit-ui#136 review). Mirrors the same switchMap pattern
   *  already used for MainFacade.openResultRequest$/FilesService.imageOpenRequest$. */
  private readonly searchRequest$ = new Subject<ResultSearchRequest>();

  constructor(
    private resultSearchService: ResultSearchService,
    private mainFacade: MainFacade,
    private messageService: MessageService,
    @Inject(DOCUMENT) private document: Document,
  ) {
    this.searchRequest$.pipe(
      switchMap((request) =>
        this.resultSearchService.search(request).pipe(
          map((rows) => ({ request, rows })),
          catchError(() => {
            this.loading = false;
            this.hasMore = false;
            this.messageService.add({
              key: 'app-toast', severity: 'error', summary: 'Search Failed', detail: 'Could not load results.',
            });
            return EMPTY;
          }),
        )
      ),
    ).subscribe(({ request, rows }) => {
      // Append/replace by the REQUEST's own page number, not by whatever
      // search()/loadMore() last did to `this.rows` - switchMap already
      // guarantees only the newest request's response ever reaches here,
      // but this keeps the two decisions (which page was this? what do we
      // do with its rows?) tied to the same request instead of relying on
      // component state that could have moved on in the meantime.
      this.rows = request.pageNo === 0 ? rows : [...this.rows, ...rows];
      this.hasMore = rows.length === PAGE_SIZE;
      this.loading = false;
    });
  }

  ngOnInit(): void {
    this.refreshRowMenuItems();
    this.search();
  }

  iconFor(row: ResultSearchRow): string {
    return iconForFileName(row.fileName);
  }

  toolName(row: ResultSearchRow): string {
    return getToolByServerProcessType(row.tool)?.displayName ?? row.tool;
  }

  /** The File cell's tooltip: the gs:// URI, plus the size when the server
   *  supplied one (see ResultSearchRow.size's doc - a rolling deploy can
   *  still omit it, and the pipe renders that as '' rather than "NaN"). */
  fileTooltip(row: ResultSearchRow): string {
    const size = new FormatFileSizePipe().transform(row.size);
    return size ? `${row.resultUri} — ${size}` : row.resultUri;
  }

  search(): void {
    this.pageNo = 0;
    this.rows = [];
    // Otherwise a row excluded by the new filters/scope stays "selected":
    // "Show in Files Tab" (and the context menu's own entry) stayed enabled
    // and pointed at a result no longer visible in the table (jit-ui#136
    // review).
    this.selectedRow = null;
    this.loadPage();
  }

  clear(): void {
    this.fileName = '';
    this.tool = null;
    this.from = null;
    this.to = null;
    this.search();
  }

  /** Mirrors RulesComponent.setScope() exactly - see rules.component.ts. */
  setScope(scope: 'mine' | 'all'): void {
    if (this.scope === scope) return;
    this.scope = scope;
    this.search();
  }

  loadMore(): void {
    this.pageNo++;
    this.loadPage();
  }

  openRow(row: ResultSearchRow): void {
    this.selectedRow = row;

    // Resolving a row (finding/switching to its project, then walking the
    // tree down to it - see MainFacade.openResultPath) can take a visible
    // moment for a deeply-nested result, with nothing on screen to show it's
    // working until the viewer's own progress bar takes over once loading
    // actually starts - a busy cursor bridges that silent gap. Subscribed
    // BEFORE calling openResultPath so a synchronous same-tick loading flag
    // (e.g. the target folder's children are already loaded) isn't missed.
    this.awaitLoadingStart();

    // openResultPath (not navigateToResults) because a search result's
    // bucket is frequently NOT the currently-selected project - results are
    // searched across every project the user can access. It switches to the
    // right project first if needed, then shows the file in the main viewer
    // without leaving the Results tab (switchToFilesTab: false) or selecting
    // it in the (hidden) file tree.
    this.mainFacade.openResultPath(row.resultUri, false);
  }

  /** Selects the row's file in the Files tab's tree, the same way a Search-tab
   *  or deep-link open does - unlike openRow(), which stays on this tab. */
  openInTree(): void {
    if (!this.selectedRow) return;
    this.mainFacade.openResultPath(this.selectedRow.resultUri);
  }

  /** Rebuilds rowMenuItems - call right before the context menu opens (its
   *  (contextmenu) handler) so it reflects current state without rebuilding
   *  on every change-detection tick (see rowMenuItems' doc above). The
   *  Mine/All entry shows the CURRENT scope, same as the toolbar's toggle -
   *  not the state a click would switch to - so the two always read as the
   *  same single toggle rather than two out-of-sync controls. */
  refreshRowMenuItems(): void {
    this.rowMenuItems = [
      { label: 'Clear', icon: 'pi pi-filter-slash', command: () => this.clear() },
      {
        label: this.scope === 'all' ? 'All' : 'Mine',
        icon: 'pi pi-users',
        command: () => this.setScope(this.scope === 'all' ? 'mine' : 'all'),
      },
      { label: 'Refresh', icon: 'pi pi-refresh', command: () => this.search() },
      {
        label: 'Show in Files Tab',
        icon: 'pi pi-sitemap',
        disabled: !this.selectedRow,
        command: () => this.openInTree(),
      },
    ];
  }

  private awaitLoadingStart(): void {
    this.setBusyCursor(true);
    merge(
      this.mainFacade.isTreeLoading$(),
      this.mainFacade.isFileLoading$(),
      this.mainFacade.isImageLoading$(),
      // Covers the case none of the three above ever fire: openResultPath
      // failed (its own error toast already showed) or resolved to a file
      // type that starts no tree/file/image loading. Without this, the
      // cursor only cleared after the full 15s timeout below, making the UI
      // look busy long after the operation actually ended (jit-ui#136
      // review). Safe alongside the real flags: they flip synchronously
      // inside setSelectedNode(), so take(1) already resolves via one of
      // them first whenever one actually starts.
      this.mainFacade.getResultResolutionSettled$(),
    ).pipe(
      filter((loading) => loading),
      take(1),
      timeout(15000),
      catchError(() => of(null)),
    ).subscribe(() => this.setBusyCursor(false));
  }

  private setBusyCursor(busy: boolean): void {
    this.document.body.classList.toggle('cursor-wait', busy);
  }

  private loadPage(): void {
    this.loading = true;
    const request: ResultSearchRequest = {
      fileName: this.fileName.trim() || undefined,
      tool: this.tool ?? undefined,
      from: this.from ? this.from.toISOString() : undefined,
      // endOfDay, not toISOString() directly: `to` is a date-only calendar
      // pick (local midnight), but it's used as an INCLUSIVE upper bound -
      // sending midnight excluded nearly everything from the selected end
      // day itself (jit-ui#136 review).
      to: this.to ? this.endOfDay(this.to).toISOString() : undefined,
      pageNo: this.pageNo,
      pageSize: PAGE_SIZE,
      scope: this.scope,
    };
    this.searchRequest$.next(request);
  }

  /** The last instant of `date`'s LOCAL calendar day (23:59:59.999), for
   *  serializing an inclusive "to" filter bound - see loadPage(). */
  private endOfDay(date: Date): Date {
    const end = new Date(date);
    end.setHours(23, 59, 59, 999);
    return end;
  }
}
